import { createContext, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { auth } from "../firebase";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut
} from "firebase/auth";
import { decodeJwt }     from "../utils/jwtUtils";
import { validateAccess } from "../services/authService";
import useIdleTimeout    from "../hooks/useIdleTimeout";
import apiClient         from "../services/apiClient";

const IDLE_TIMEOUT_MS = 5 * 60 * 1000;

const ROLES = {
  SUPER_ADMIN: 1,
  USER_ADMIN:  2,
};

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {

  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(null);
  const [loading, setLoading] = useState(true);

  const loginInProgressRef = useRef(false);
  const sessionExpiredRef  = useRef(false);

  useEffect(() => {

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {

if (loginInProgressRef.current) return;

      if (currentUser) {

        const idToken = await currentUser.getIdToken();

        setUser(currentUser);
        setToken(idToken);

      } else {

        setUser(null);
        setToken(null);

      }

      setLoading(false);

    });

    return () => unsubscribe();

  }, []);

  const logout = useCallback(async () => {

    sessionExpiredRef.current = false;

    await signOut(auth);

    setUser(null);
    setToken(null);

  }, []);

  useEffect(() => {

    const interceptorId = apiClient.interceptors.request.use(
      async (config) => {

        if (sessionExpiredRef.current) {
          try { await signOut(auth); } catch (_) {}
          sessionExpiredRef.current = false;
          window.location.href = '/';
          return Promise.reject(
            Object.assign(new Error('La sesión ha expirado por inactividad.'), {
              code: 'SESSION_EXPIRED',
            })
          );
        }

        return config;

      },
      (error) => Promise.reject(error)
    );

    return () => apiClient.interceptors.request.eject(interceptorId);

  }, [logout]);

  const login = async (email, password) => {

    loginInProgressRef.current = true;
    sessionExpiredRef.current  = false;

    try {

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser   = userCredential.user;
      const idToken        = await firebaseUser.getIdToken(true);

      await validateAccess();

      setUser(firebaseUser);
      setToken(idToken);

      return firebaseUser;

    } catch (err) {

      if (auth.currentUser) {
        try { await signOut(auth); } catch (_) {}
      }

      throw err;

    } finally {

      loginInProgressRef.current = false;

    }

  };

  const getFreshToken = useCallback(async () => {

    if (!auth.currentUser) return null;

    try {

      const freshToken = await auth.currentUser.getIdToken(true);
      setToken(freshToken);
      return freshToken;

    } catch (error) {

      console.error('Error refreshing token', error);
      logout();
      return null;

    }

  }, [logout]);

  useIdleTimeout(
    () => { sessionExpiredRef.current = true; },
    IDLE_TIMEOUT_MS,
    !!user
  );

  const decodedToken = useMemo(() => decodeJwt(token), [token]);

  const role = decodedToken?.role ?? null;

  const isSuperAdmin  = role === ROLES.SUPER_ADMIN;
  const isUserAdmin   = role === ROLES.USER_ADMIN;

  const canWrite             = decodedToken?.permissions?.includes("write") ?? false;
  const canAccessUserAdmin   = [ROLES.SUPER_ADMIN, ROLES.USER_ADMIN].includes(role);
  const canAccessDashboard   = [ROLES.SUPER_ADMIN, ROLES.USER_ADMIN].includes(role);
  const canAccessPriceSync   = [ROLES.SUPER_ADMIN].includes(role);
  const canAccessHistorySync = [ROLES.SUPER_ADMIN, ROLES.USER_ADMIN].includes(role);

  const value = {

    user,
    token,
    loading,

    login,
    logout,
    getFreshToken,

    isAuthenticated: !!user,
    decodedToken,
    userRole: role,

    isSuperAdmin,
    isUserAdmin,

    canWrite,
    canAccessUserAdmin,
    canAccessDashboard,
    canAccessPriceSync,
    canAccessHistorySync,

  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );

};
